import React, { useCallback, useEffect, useState } from "react";
import { Bell, Loader2, Megaphone, Pencil, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, apiError } from "../../lib/api";
import { formatDateTime, relativeTime } from "../../lib/format";
import { cn } from "../../lib/utils";

const blank = { id: null, title: "", message: "", audience: "all", userIds: [] };

export default function AdminNotifications() {
  const [draft, setDraft] = useState(blank);
  const [clients, setClients] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [state, setState] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    api
      .get("/api/admin/clients", { params: { limit: 100, page: 1 } })
      .then(({ data }) => setClients(data.items))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/notifications", { params: { page, limit: 10 } });
      setState(data);
    } catch (error) {
      toast.error(apiError(error, "Could not load notifications"));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleClient = (id) =>
    setDraft((d) => ({
      ...d,
      userIds: d.userIds.includes(id) ? d.userIds.filter((x) => x !== id) : [...d.userIds, id],
    }));

  const submit = async (event) => {
    event.preventDefault();
    if (draft.title.trim().length < 2 || draft.message.trim().length < 2) {
      toast.error("Add a title and a message before sending");
      return;
    }
    if (draft.audience === "selected" && draft.userIds.length === 0) {
      toast.error("Select at least one client");
      return;
    }
    setBusy(true);
    try {
      const body = {
        title: draft.title.trim(),
        message: draft.message.trim(),
        audience: draft.audience,
        userIds: draft.audience === "selected" ? draft.userIds : [],
      };
      if (draft.id) {
        await api.put(`/api/admin/notifications/${draft.id}`, body);
        toast.success("Notification updated");
      } else {
        const { data } = await api.post("/api/admin/notifications", body, { params: { sendEmail } });
        toast.success(
          sendEmail
            ? `Notification published and emailed to ${data.emailed} recipient(s)`
            : "Notification published to the client portal"
        );
      }
      setDraft(blank);
      setSendEmail(false);
      await load();
    } catch (error) {
      toast.error(apiError(error, "Could not publish the notification"));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/api/admin/notifications/${id}`);
      toast.success("Notification deleted");
      if (draft.id === id) setDraft(blank);
      await load();
    } catch (error) {
      toast.error(apiError(error, "Could not delete the notification"));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Market notifications"
        subtitle="Publish updates to the investor portal, optionally by email."
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,420px)_1fr]">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">
              {draft.id ? "Edit notification" : "Compose notification"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={draft.title}
                  maxLength={160}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="Daily market update"
                  data-testid="admin-notification-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  rows={6}
                  value={draft.message}
                  maxLength={4000}
                  onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))}
                  placeholder="Markets closed higher today. Daily profit has been credited to your account."
                  data-testid="admin-notification-message-input"
                />
                <p className="text-xs text-muted-foreground">{draft.message.length} / 4000</p>
              </div>
              <div className="space-y-2">
                <Label>Audience</Label>
                <Select
                  value={draft.audience}
                  onValueChange={(v) => setDraft((d) => ({ ...d, audience: v }))}
                >
                  <SelectTrigger data-testid="admin-notification-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All clients</SelectItem>
                    <SelectItem value="selected">Selected clients</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {draft.audience === "selected" ? (
                <div className="space-y-2">
                  <Label>Recipients ({draft.userIds.length} selected)</Label>
                  <div
                    className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border p-2"
                    data-testid="admin-notification-recipients"
                  >
                    {clients.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">No clients available.</p>
                    ) : (
                      clients.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleClient(c.id)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                            draft.userIds.includes(c.id)
                              ? "bg-primary/15 text-primary"
                              : "hover:bg-accent"
                          )}
                          data-testid={`admin-notification-recipient-${c.id}`}
                        >
                          <span className="min-w-0 truncate">{c.name}</span>
                          <span className="ml-3 shrink-0 text-xs text-muted-foreground">{c.email}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : null}

              {!draft.id ? (
                <div className="flex items-center justify-between rounded-lg border border-border surface-2 p-3">
                  <div>
                    <p className="text-sm font-medium">Also send by email</p>
                    <p className="text-xs text-muted-foreground">
                      Delivers the same message to recipients&apos; inboxes.
                    </p>
                  </div>
                  <Switch
                    checked={sendEmail}
                    onCheckedChange={setSendEmail}
                    data-testid="admin-notification-email-switch"
                  />
                </div>
              ) : null}

              <div className="flex gap-2">
                <Button type="submit" disabled={busy} data-testid="admin-notification-send-button">
                  {busy ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {draft.id ? "Save changes" : "Publish notification"}
                </Button>
                {draft.id ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setDraft(blank)}
                    data-testid="admin-notification-cancel-edit"
                  >
                    Cancel
                  </Button>
                ) : null}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Published notifications</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3" data-testid="page-loading-skeleton">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full rounded-xl" />
                ))}
              </div>
            ) : state.items.length === 0 ? (
              <EmptyState
                icon={Megaphone}
                title="Nothing published yet"
                description="Compose your first market update; it will appear instantly in the investor portal."
                className="border-0 bg-transparent"
                testId="admin-notifications-empty"
              />
            ) : (
              <ul className="space-y-3">
                {state.items.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-xl border border-border p-4"
                    data-testid={`admin-notification-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{item.title}</p>
                        <p className="mt-1 line-clamp-2 whitespace-pre-line text-sm text-muted-foreground">
                          {item.message}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setDraft({
                              id: item.id,
                              title: item.title,
                              message: item.message,
                              audience: item.audience,
                              userIds: item.userIds || [],
                            })
                          }
                          data-testid={`admin-notification-edit-${item.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmDelete(item.id)}
                          data-testid={`admin-notification-delete-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5">
                        {item.audience === "all" ? <Users className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                        {item.audience === "all" ? "All clients" : `${item.recipientCount} selected`}
                      </span>
                      <span>{item.readCount} read</span>
                      <span>{formatDateTime(item.createdAt)}</span>
                      <span>{relativeTime(item.createdAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {state.pages > 1 ? (
              <div className="mt-4 flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {page} of {state.pages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= state.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this notification?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be removed from every client&apos;s notification list. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="admin-notification-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
